import { cn } from "../../lib/cn";

export interface ToggleProps {
  /** Current on/off state. */
  checked: boolean;
  /** Fired with the new state when the user clicks. */
  onCheckedChange: (next: boolean) => void;
  /** Required for screen readers — the inner button has no visible label. */
  "aria-label": string;
  /** Disable the toggle (visually + interaction). */
  disabled?: boolean;
  /** Optional className spliced onto the wrapper button. */
  className?: string;
  /** Optional `data-testid` for tests. */
  "data-testid"?: string;
}

/**
 * `<Toggle>` — pill switch with `role="switch"` semantics.
 *
 * Wave 1.5 promotion. Consolidates RulesPage's `ToggleSwitch` (the
 * pill-with-knob switch used on the constraint toggles) into a shared
 * primitive. The TeamPage's `ToggleRow` (which is actually a labelled
 * checkbox row, not a pill) remains inline because it's a different
 * shape — the spec calls these out together but the visual primitives
 * differ; only the pill switch was promoted.
 *
 * ON-state colour aligns to the `Chip` `accent` `active` state
 * (`bg-accent-soft` + `border-accent/40` would be too low-contrast for
 * a switch where state legibility is critical, so we use the stronger
 * `bg-accent-strong` + `border-accent` pair). The knob reaches for the
 * `--color-fg-on-accent` semantic token so it renders white-on-deep-accent
 * across every pastel theme + bright on slate-dark — closes the W1.5
 * drift item ("`bg-bg` knob breaks on light themes").
 */
export function Toggle({
  checked,
  onCheckedChange,
  disabled,
  className,
  "aria-label": ariaLabel,
  "data-testid": testId,
}: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      data-testid={testId}
      className={cn(
        "relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full border",
        // #2172 — the track's colour eases with the knob's slide.
        "transition-colors duration-[var(--dur-med)] ease-[var(--ease-out)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-bg",
        "disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "border-accent bg-accent-strong" : "border-border bg-bg-subtle",
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          // #2172 — the knob slides on the tokens' ease-out (D4: calm, no
          // overshoot).
          "inline-block h-3.5 w-3.5 transform rounded-full shadow",
          "transition-[transform,background-color] duration-[var(--dur-med)] ease-[var(--ease-out)]",
          // ON-state knob uses fg-on-accent so it stays bright on the
          // deep accent track. OFF-state knob uses bg-elevated so it
          // reads as a card-coloured handle on the bg-subtle track.
          checked ? "bg-fg-on-accent" : "bg-bg-elevated",
          checked ? "translate-x-[18px]" : "translate-x-0.5",
        )}
      />
    </button>
  );
}
